import type { DirectusDto, ShopApiDto } from '@do/data-transfer-objects';

import { Api } from '@shared/api';

import { toQueryStringParams } from '@widgets/artworks-filter-sidebar/configs';
import type { ArtworksFiltersOptionsResponse, FormValues } from '@widgets/artworks-filter-sidebar/types';

import type { ArtAssetListItem, ArtAssetReadManyResponse, ArtAssetsReadManyArguments, UfdaPhotos } from '../types';

export class ArtAssetCardApi extends Api {
  public async readOne(id: string | number): Promise<ShopApiDto.ReadOneArtAssetResponseNew> {
    const res = await this.fetch(`${this.baseUrl}/art-assets/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res.json();
  }

  public async readMany({
    page,
    loadMore,
    institutionId,
    perPage,
    order,
    offset,
    sort,
    search,
    artists,
    medium,
    styles,
    countries,
    token_status,
    price,
    artists_slugs,
    styles_slugs,
    medium_slugs,
    countries_slugs,
    curators,
    curator_review,
    creation_year,
    original_size_x,
    original_size_y,
  }: ArtAssetsReadManyArguments): Promise<ArtAssetReadManyResponse> {
    const perPageMultiplier: number = loadMore ? +loadMore + 1 : 1;

    const artAssetQuery = new URLSearchParams();
    artAssetQuery.append('offset', `${offset || (page - 1) * perPage}`);
    artAssetQuery.append('limit', `${perPage * perPageMultiplier}`);
    artAssetQuery.append('institutions', institutionId.toString());
    if (sort) artAssetQuery.append('column', `${sort}`);
    if (search) artAssetQuery.append('search', search);
    if (order) artAssetQuery.append('order', `${order}`);
    if (artists) artAssetQuery.append('artists', artists);
    if (medium) artAssetQuery.append('medium', medium);
    if (styles) artAssetQuery.append('styles', styles);
    if (countries) artAssetQuery.append('countries', countries);
    if (token_status) artAssetQuery.append('token_status', token_status);
    if (price) artAssetQuery.append('price', price);
    if (curators) artAssetQuery.append('curators', curators);
    if (artists_slugs) artAssetQuery.append('artists_slugs', artists_slugs);
    if (medium_slugs) artAssetQuery.append('medium_slugs', medium_slugs);
    if (styles_slugs) artAssetQuery.append('styles_slugs', styles_slugs);
    if (countries_slugs) artAssetQuery.append('countries_slugs', countries_slugs);
    if (curator_review) artAssetQuery.append('curator_review', curator_review.toString());
    if (creation_year) artAssetQuery.append('creation_year', creation_year);
    if (original_size_x) artAssetQuery.append('original_size_x', original_size_x);
    if (original_size_y) artAssetQuery.append('original_size_y', original_size_y);

    const response = await this.fetch(`${this.baseUrl}/art-assets?${artAssetQuery}`).then((resp) => resp.json());

    return {
      artworks: response.art_assets,
      totalCount: response.total_count,
    };
  }

  public async readTotalCount(config: Record<string, string> | URLSearchParams): Promise<number> {
    return await this.fetch(`${this.baseUrl}/art-assets/total-count?${config}`).then((resp) => resp.json());
  }

  public async readManySimilar(id: string | number): Promise<ShopApiDto.ReadManySimilarArtAssetsResponse> {
    const res = await this.fetch(`${this.baseUrl}/art-assets/${id}/similar?limit=4&offset=0`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res.json();
  }

  public async readTilesData(id: string): Promise<DirectusDto.TileDto[]> {
    const response = await this.fetch(`${this.baseUrl}/tiles/${id}`);

    if (!response.ok) {
      return [];
    } else {
      return response.json();
    }
  }

  public async readUfdaPhotos(): Promise<UfdaPhotos> {
    const res = await this.fetch(`${this.baseUrl}/art-assets/ufda-photos`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res.json();
  }

  public async readFiltersOptions(
    values?: FormValues,
    initialValues?: FormValues,
  ): Promise<ArtworksFiltersOptionsResponse> {
    const query = toQueryStringParams(values, initialValues);

    const response = await this.fetch(`${this.baseUrl}/art-assets/filter-options?${query}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await response.json();
  }

  public async readPurchased(wallet: string): Promise<ArtAssetListItem[]> {
    const res = await this.fetch(`${this.baseUrl}/art-assets/bought?address=${wallet}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return res.json();
  }
}
